import {
  type AiVideoTags,
  EventSeverity,
  EventTrigger,
  Location,
  type Media,
  type MediaEvent,
  MediaRequestStatusFilterV2,
  MediaStatus,
  MediaType,
  type SafetyOccurrence,
  SafetyReviewDismissReason,
  SafetyReviewStatus,
  SuggestedOccurrence,
} from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import type { MediaRequestData } from '@cobliteam/media/src/types/MediaRequest';
import { endOfDay, startOfDay, subMonths } from '@cobliteam/utils/date';

export const DEFAULT_MEDIA_REQUEST_STATUS = [MediaRequestStatusFilterV2.PENDING, MediaRequestStatusFilterV2.COMPLETED];

export const getMediaSuggestedOccurrencesLabel = (suggestedOccurrence: SuggestedOccurrence, t: (key: string) => string) => {
  switch (suggestedOccurrence) {
    case SuggestedOccurrence.IS_DAYTIME:
      return t('media:suggested_occurrences.day');
    case SuggestedOccurrence.IS_AFTERNOON:
      return t('media:suggested_occurrences.afternoon');
    case SuggestedOccurrence.IS_NIGHT:
      return t('media:suggested_occurrences.night');
    case SuggestedOccurrence.IS_SUNNY:
      return t('media:suggested_occurrences.sunny');
    case SuggestedOccurrence.IS_CLOUDY:
      return t('media:suggested_occurrences.cloudy');
    case SuggestedOccurrence.IS_RAINING:
      return t('media:suggested_occurrences.raining');
    case SuggestedOccurrence.IS_HEAVY_TRAFFIC:
      return t('media:suggested_occurrences.heavy_traffic');
    case SuggestedOccurrence.HAS_PASSENGER:
      return t('media:suggested_occurrences.has_passenger');
    case SuggestedOccurrence.IS_DIRT_ROAD:
      return t('media:suggested_occurrences.dirt_road');
    case SuggestedOccurrence.IS_USING_PHONE:
      return t('media:suggested_occurrences.is_using_phone');
    default:
      return t('media:suggested_occurrences.unknown');
  }
};

export const useDismissReasonLabels = () => {
  const { t } = useTranslation('media');

  return {
    [SafetyReviewDismissReason.NO_EVENT_OR_OCCURRENCE]: t('dismiss_dialog.reasons.no_event_or_occurrence'),
    [SafetyReviewDismissReason.WRONG_EVENT_OR_OCCURRENCE]: t('dismiss_dialog.reasons.wrong_event_or_occurrence'),
    [SafetyReviewDismissReason.BLURRY_OR_LOW_QUALITY]: t('dismiss_dialog.reasons.blurry_or_low_quality'),
    [SafetyReviewDismissReason.EVENT_NOT_INTERESTING]: t('dismiss_dialog.reasons.event_not_interesting'),
  };
};

// Hook wrapper for getMediaSuggestedOccurrencesLabel
export const useMediaSuggestedOccurrencesLabel = () => {
  const { t } = useTranslation('media');
  return (suggestedOccurrence: SuggestedOccurrence) => getMediaSuggestedOccurrencesLabel(suggestedOccurrence, t);
};

export enum MediaTableType {
  EVENT_MEDIA = 'EVENT_MEDIA',
  FOOTAGE_MEDIA = 'FOOTAGE_MEDIA',
}

export const getMediaStatusBasedOnReviewStatus = (reviewStatus?: SafetyReviewStatus) => {
  switch (reviewStatus) {
    case SafetyReviewStatus.RELEVANT:
      return MediaStatus.RELEVANT;
    case SafetyReviewStatus.DISMISSED:
      return MediaStatus.DISMISSED;
    default:
      return MediaStatus.WATCHED;
  }
};

export enum EventTableType {
  DASHBOARD_EVENT_MEDIA = 'DASHBOARD_EVENT_MEDIA',
  EVENT_MEDIA = 'EVENT_MEDIA',
  FAVORITE_EVENT_MEDIA = 'FAVORITE_EVENT_MEDIA',
}

export enum RequestTableType {
  REQUEST_MEDIA = 'REQUEST_MEDIA',
  FAVORITE_REQUEST_MEDIA = 'FAVORITE_REQUEST_MEDIA',
}

export enum TripImageHistoryTableType {
  ALL_TRIPS = 'ALL_TRIPS',
  PENDING_NOMINATE_TRIPS = 'PENDING_NOMINATE_TRIPS',
}

export const getMediaRequestTab = (currentRoute: string) => {
  switch (currentRoute) {
    case Location.CAMERA_REQUESTS_FAVORITES:
      return RequestTableType.FAVORITE_REQUEST_MEDIA;
    default:
      return RequestTableType.REQUEST_MEDIA;
  }
};

export const getSeverityTagLabel = (t: (key: string) => string, severity?: EventSeverity) => {
  switch (severity) {
    case EventSeverity.HIGHER:
      return t('media:severity.higher');
    case EventSeverity.HIGH:
      return t('media:severity.high');
    case EventSeverity.MEDIUM:
      return t('media:severity.medium');
    case EventSeverity.LOW:
      return t('media:severity.low');
    default:
      return t('media:severity.high');
  }
};

export const DEFAULT_MEDIA_REQUEST_DATA: MediaRequestData = {
  hasNotificationEnabled: false,
};

export const MEDIA_DEFAULT_DURATION_IN_MILLIS = 60000;

export const DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY = [true, false];

export const MEDIA_REQUEST_MODAL_BASE_PERIOD = {
  before: startOfDay(subMonths(new Date(), 6)),
  after: endOfDay(new Date()),
};

export const MEDIA_REQUEST_MODAL_TRIPS_LIMIT = {
  min: 0,
  max: 10000,
};

export const getMediaEventLabel = (eventTrigger: EventTrigger, t: (key: string) => string, splitCollisionEvents = false) => {
  switch (eventTrigger) {
    case EventTrigger.HARSH_BREAKING:
      return t('media:event_triggers.harsh_breaking');
    case EventTrigger.HARSH_ACCELERATION:
      return t('media:event_triggers.harsh_acceleration');
    case EventTrigger.HARSH_CORNERING:
    case EventTrigger.LEFT_TURN_SPEEDING:
      return t('media:event_triggers.harsh_cornering');
    case EventTrigger.COLLISION:
      return t('media:event_triggers.collision');
    case EventTrigger.TRAFFIC_SPEED_VIOLATED:
      return t('media:event_triggers.traffic_speed_violated');
    case EventTrigger.STOP_SIGN_VIOLATED:
      return t('media:event_triggers.stop_sign_violated');
    case EventTrigger.LANE_DRIFT:
      return t('media:event_triggers.lane_drift');
    case EventTrigger.FRONT_COLLISION_WARNING:
      // Sem o split os dois são um evento só para o usuário, e o nome que sobra é o do tailgating —
      // "Distância insegura" descreve Headway baixo, não a aproximação que caracteriza o FCW.
      return splitCollisionEvents ? t('media:event_triggers.front_collision_warning') : t('media:event_triggers.tailgating');
    case EventTrigger.TAILGATING:
      return t('media:event_triggers.tailgating');
    case EventTrigger.ANOMALY:
      return t('media:event_triggers.anomaly');
    case EventTrigger.DISTRACTED_DRIVING:
      return t('media:event_triggers.distracted_driving');
    case EventTrigger.YAWN:
      return t('media:event_triggers.yawn');
    case EventTrigger.EYES_CLOSED:
      return t('media:event_triggers.eyes_closed');
    case EventTrigger.PHONE_USAGE:
      return t('media:event_triggers.phone_usage');
    case EventTrigger.SMOKING:
      return t('media:event_triggers.smoking');
    case EventTrigger.MAX_SPEED_EXCEEDED:
      return t('media:event_triggers.max_speed_exceeded');
    case EventTrigger.SOS:
      return t('media:event_triggers.sos');
    case EventTrigger.PEDESTRIAN_COLLISION:
      return t('media:event_triggers.pedestrian_collision');
    case EventTrigger.NOT_WEARING_SEAT_BELTS:
      return t('media:event_triggers.not_wearing_seat_belts');
    case EventTrigger.IDLE_SPEED:
      return t('media:event_triggers.idle_speed');
    case EventTrigger.LOW_VOLTAGE:
      return t('media:event_triggers.low_voltage');
    case EventTrigger.ILLEGAL_SHUTDOWN:
      return t('media:event_triggers.illegal_shutdown');
    case EventTrigger.TEMPERATURE_ABNORMAL:
      return t('media:event_triggers.temperature_abnormal');
    case EventTrigger.INSUFFICIENT_ELECTRICITY_ALARM:
      return t('media:event_triggers.insufficient_electricity_alarm');
    case EventTrigger.VIDEO_BLOCK:
      return t('media:event_triggers.video_block');
    case EventTrigger.SPEED_LIMIT_SIGN:
      return t('media:event_triggers.speed_limit_sign');
    case EventTrigger.PASSENGER_NOT_WEARING_SEAT_BELTS:
      return t('media:event_triggers.passenger_not_wearing_seat_belts');
    case EventTrigger.SERIOUS_THEFT:
      return t('media:event_triggers.serious_theft');
    case EventTrigger.ANTI_DESTRUCTION:
      return t('media:event_triggers.anti_destruction');
    case EventTrigger.EATING:
      return t('media:event_triggers.eating');
    case EventTrigger.OVERCROWDING:
      return t('media:event_triggers.overcrowding');
    case EventTrigger.G_SENSOR_WAKEUP:
      return t('media:event_triggers.g_sensor_wakeup');
    case EventTrigger.MISSINGFACE:
      return t('media:event_triggers.missingface');
    default:
      return t('media:event_triggers.unknown');
  }
};

// O wrapper de hook vive em ./useMediaEventLabel — ver o porquê lá.

export const mediaTypeLabels: { [key in MediaType]: string } = {
  [MediaType.EVENT_VIDEO]: 'media:media_types.event_video',
  [MediaType.FOOTAGE_VIDEO]: 'media:media_types.footage_video',
  [MediaType.TIMELAPSE]: 'media:media_types.timelapse',
};

// Hook wrapper for mediaTypeLabels
export const useMediaTypeLabels = () => {
  const { t } = useTranslation('media');
  return {
    [MediaType.EVENT_VIDEO]: t('media:media_types.event_video'),
    [MediaType.FOOTAGE_VIDEO]: t('media:media_types.footage_video'),
    [MediaType.TIMELAPSE]: t('media:media_types.timelapse'),
  } as { [key in MediaType]: string };
};

export const isNoSeatbeltOccurrence = ({ name }: SafetyOccurrence) => name === 'Motorista sem cinto';

/**
 * Refines the safety occurrences by handling fallbacks (e.g., default occurrence based on event trigger) and special cases (e.g., SOS events).
 */
export const getMediaSafetyOccurrences = (t: (key: string) => string, media?: Media, occurrences?: SafetyOccurrence[], aiVideoTags?: AiVideoTags): SafetyOccurrence[] => {
  const { safetyReview, mediaDetails } = media || {};
  const eventTrigger = (mediaDetails as MediaEvent)?.eventTrigger;
  const mediaOccurrences = safetyReview?.occurrences;

  if (mediaOccurrences) return mediaOccurrences;
  if (!eventTrigger || eventTrigger === EventTrigger.SOS) return [];

  const mediaEventLabel = getMediaEventLabel(eventTrigger, t);

  const defaultOccurrences: SafetyOccurrence[] = [];

  // Add fallback for event media based on event trigger
  const [triggerOccurrence] =
    occurrences?.filter((occurrence) => {
      return occurrence.name.toLowerCase() === mediaEventLabel.toLowerCase();
    }) || [];

  if (triggerOccurrence) defaultOccurrences.push(triggerOccurrence);

  // Include AI-detected occurrences
  if (aiVideoTags?.seatbeltDetection?.withSeatbelt === false) {
    const [seatbeltOccurrence] = occurrences?.filter(isNoSeatbeltOccurrence) || [];

    if (seatbeltOccurrence) defaultOccurrences.push(seatbeltOccurrence);
  }

  return defaultOccurrences;
};
